import { cn } from '@repo/ui/lib/utils'
import { Link } from '@tanstack/react-router'
import { ExternalLink } from 'lucide-react'
import React from 'react'
import { NavAnchor } from '../nav/shared'
import { FooterLayout } from './layout'
import type { FooterContent, FooterLink } from './types'

interface FooterProps {
  content: FooterContent
  className?: string
}

const renderLink = (
  link: FooterLink | { text: string; href?: string; onClick?: () => void; external?: boolean },
  options?: { className?: string; fullWidth?: boolean; align?: 'left' | 'right'; muted?: boolean }
) => {
  const { text, href, external, onClick } = link
  const { className, fullWidth = true, align = 'left', muted = false } = options ?? {}

  const label = (
    <span className="inline-flex items-center gap-1">
      {text}
      {external && <ExternalLink className="h-3 w-3" aria-hidden="true" />}
    </span>
  )

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    onClick?.(event)
  }

  return (
    <NavAnchor
      key={`${text}-${href ?? onClick?.name ?? 'action'}`}
      href={href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      onClick={handleClick}
      className={cn(
        'inline-flex gap-1 text-sm transition-colors',
        muted
          ? 'text-muted-foreground hover:text-foreground'
          : 'text-foreground hover:text-foreground',
        fullWidth ? 'w-full justify-start' : 'justify-start',
        align === 'right' ? 'justify-end text-right' : 'justify-start',
        className
      )}
    >
      {label}
    </NavAnchor>
  )
}

export const FooterFull: React.FC<FooterProps> = ({ content, className }) => {
  const { brand, description, sections = [], social = [], copyright, bottomLinks = [] } = content

  return (
    <FooterLayout className={className}>
      <div className="border-t border-border/35 py-9 md:py-11">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.25fr_0.75fr]">
          {(brand || description || social.length > 0) && (
            <div>
              {brand && (
                <Link to="/" className="flex items-center gap-2">
                  {brand.logo && <div className="flex items-center">{brand.logo}</div>}
                  <span className="px-2 text-lg font-semibold tracking-tight">{brand.name}</span>
                </Link>
              )}

              {description && (
                <p className="mb-5 max-w-md py-4 leading-relaxed text-muted-foreground">
                  {description}
                </p>
              )}

              {social.length > 0 && (
                <div className="flex items-center gap-3">
                  {social.map((socialLink) => (
                    <NavAnchor
                      key={socialLink.name}
                      href={socialLink.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="rounded-[3px] border border-border/55 bg-background/70 p-2 text-muted-foreground transition-colors hover:text-foreground"
                      aria-label={socialLink.name}
                    >
                      {React.isValidElement(socialLink.icon)
                        ? socialLink.icon
                        : React.createElement(
                            socialLink.icon as React.ComponentType<{ className?: string }>,
                            { className: 'h-5 w-5' }
                          )}
                    </NavAnchor>
                  ))}
                </div>
              )}
            </div>
          )}

          {sections.length > 0 && (
            <div className="grid grid-cols-2 gap-8 lg:grid-cols-2 lg:justify-self-end lg:pr-8">
              {sections.map((section) => (
                <div key={section.title} className="min-w-28">
                  <h4 className="mb-4 font-semibold text-foreground">{section.title}</h4>
                  <ul className="space-y-3">
                    {section.links.map((link) => (
                      <li key={`${link.text}-${link.href}`}>
                        {renderLink(link, { fullWidth: true, muted: true })}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {(copyright || bottomLinks.length > 0) && (
        <div className="border-t border-border/35 px-1 py-4">
          <div className="flex flex-col items-center justify-between gap-3 md:flex-row">
            {copyright && <p className="text-sm text-muted-foreground">{copyright}</p>}

            {bottomLinks.length > 0 && (
              <div className="flex items-center gap-5">
                {bottomLinks.map((link) =>
                  renderLink(link, { className: 'text-sm', fullWidth: false, muted: true })
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </FooterLayout>
  )
}

export type { FooterProps }
export type { FooterLink, FooterSection, SocialLink } from './types'
